import { MiniGameBase } from './base.js';

/**
 * "Find the Partner" pairing mini-game
 * Player matches 8 items (4 pairs) by clicking to select pairs
 * Similar to main board pairing mechanic
 */
export class FindPartnerGame extends MiniGameBase {
    constructor(deepDiveData, categoryName, onComplete = null, category = null) {
        super(deepDiveData, categoryName, onComplete, category);
        this.selectedItems = [];
        this.pairedIndices = [];
        this.shuffledItems = this.shuffleItems();
    }

    /**
     * Shuffle items for display
     */
    shuffleItems() {
        // Support both v1.0 (pairs) and v2.0 (items) formats
        const items = [];

        if (this.data.pairs) {
            // v1.0 format: pairs is array of arrays
            this.data.pairs.forEach((pair, pairIdx) => {
                items.push({ text: pair[0], pairIdx, itemIdx: 0 });
                items.push({ text: pair[1], pairIdx, itemIdx: 1 });
            });
        } else if (this.data.items) {
            // v2.0 format: items is array of {left, right} objects
            this.data.items.forEach((item, pairIdx) => {
                items.push({ text: item.left, pairIdx, itemIdx: 0 });
                items.push({ text: item.right, pairIdx, itemIdx: 1 });
            });
        }

        return this.shuffleArray(items);
    }

    /**
     * Render the pairing UI
     */
    render(container) {
        super.render(container);

        container.innerHTML = `
            <div class="deepdive-container">
                <div class="instruction">${this.data.instruction || 'Find the matching pairs:'}</div>
                <div class="progress">${this.getProgress()} pairs found</div>
                <div class="partner-grid">
                    ${this.shuffledItems.map((item, idx) => `
                        <div class="partner-item ${this.isPaired(idx) ? 'paired' : ''}" data-index="${idx}">
                            ${item.text}
                        </div>
                    `).join('')}
                </div>
                <div class="button-row"></div>
            </div>
        `;

        // Add skip button
        const buttonRow = container.querySelector('.button-row');
        const skipBtn = this.createSkipButton();
        buttonRow.appendChild(skipBtn);

        this.attachEventListeners();
    }

    /**
     * Check if an item is already paired
     */
    isPaired(index) {
        return this.pairedIndices.includes(index);
    }

    /**
     * Attach click listeners
     */
    attachEventListeners() {
        const items = this.container.querySelectorAll('.partner-item');

        items.forEach((el, idx) => {
            el.addEventListener('click', () => this.handleItemClick(idx));

            // Attach hint if this item matches a word in category
            const itemText = this.shuffledItems[idx]?.text;
            if (itemText) {
                this.attachHintIfExists(el, itemText);
            }
        });
    }

    /**
     * Handle item click
     */
    handleItemClick(index) {
        // Don't allow selecting already paired items
        if (this.pairedIndices.includes(index)) {
            return { action: 'already_paired' };
        }

        // Don't allow selecting same item twice
        if (this.selectedItems.includes(index)) {
            // Deselect
            this.selectedItems = this.selectedItems.filter(i => i !== index);
            this.updateUI();
            return { action: 'deselect' };
        }

        // Add to selection
        this.selectedItems.push(index);
        this.updateUI();

        // Check if we have 2 items selected
        if (this.selectedItems.length === 2) {
            const [idx1, idx2] = this.selectedItems;
            const item1 = this.shuffledItems[idx1];
            const item2 = this.shuffledItems[idx2];

            // Check if they form a valid pair
            if (item1.pairIdx === item2.pairIdx) {
                // Correct pair!
                this.pairedIndices.push(idx1, idx2);
                this.completedItems.push(item1.pairIdx);
                this.selectedItems = [];
                this.updateUI();
                this.updateProgress();
                this.playMatchSound();

                if (this.isComplete()) {
                    setTimeout(() => this.showCelebration(), 500);
                }

                return { action: 'correct_pair' };
            } else {
                // Incorrect pair
                this.showError();
                this.playErrorSound();
                setTimeout(() => {
                    this.selectedItems = [];
                    this.updateUI();
                }, 800);
                return { action: 'incorrect_pair' };
            }
        }

        return { action: 'select_item' };
    }

    /**
     * Handle generic input (for testing)
     */
    handleInput(selection) {
        if ('item' in selection) {
            return this.handleItemClick(selection.item);
        }
        return { action: 'none' };
    }

    /**
     * Update UI based on current state
     */
    updateUI() {
        if (!this.container) return;

        const items = this.container.querySelectorAll('.partner-item');
        items.forEach((el, idx) => {
            el.classList.remove('selected', 'paired', 'error');

            if (this.pairedIndices.includes(idx)) {
                el.classList.add('paired');
            } else if (this.selectedItems.includes(idx)) {
                el.classList.add('selected');
            }
        });
    }

    /**
     * Update progress display
     */
    updateProgress() {
        const progressEl = this.container?.querySelector('.progress');
        if (progressEl) {
            const totalPairs = this.data.pairs?.length || this.data.items?.length || 0;
            progressEl.textContent = `${this.completedItems.length}/${totalPairs} pairs found`;
        }
    }

    /**
     * Show error animation
     */
    showError() {
        const items = this.container.querySelectorAll('.partner-item');

        this.selectedItems.forEach(idx => {
            items[idx]?.classList.add('error');
            setTimeout(() => {
                items[idx]?.classList.remove('error');
            }, 300);
        });
    }

    /**
     * Check if game is complete
     */
    isComplete() {
        const totalPairs = this.data.pairs?.length || this.data.items?.length || 0;
        return this.completedItems.length === totalPairs;
    }

    /**
     * Get progress string
     */
    getProgress() {
        const total = this.data.pairs?.length || this.data.items?.length || 0;
        const completed = this.completedItems.length;
        return `${completed}/${total}`;
    }

    /**
     * Show celebration
     */
    showCelebration() {
        super.showCelebration();
        // Could add more elaborate celebration here
    }
}
